import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from "chart.js";
import { formatInTimeZone } from 'date-fns-tz';


ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale);

const SUPABASE_URL = "https://escjwccxbbdacgqykrqw.supabase.co/rest/v1/smartbuoy";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzY2p3Y2N4YmJkYWNncXlrcnF3Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NjE5NjIyMiwiZXhwIjoyMDYxNzcyMjIyfQ.azC7n_nGBFdaT98XBEUiJrsbdMyQTW9ynIKmB9dg_kk";

type LouraItem = {
  data: string;
  timestamp?: string;
};

const Soda: React.FC = () => {
  const [data, setData] = useState<LouraItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const response = await fetch(`${SUPABASE_URL}?order=id.desc&limit=10`, {
        headers: {
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const result: LouraItem[] = await response.json();
      setData(result);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const parseRawData = (item: LouraItem) => {
    try {
      const fixedStr = item.data.trim().replace(/'/g, '"');
      const parsed = JSON.parse(fixedStr);
      return { ...parsed, timestamp: item.timestamp };
    } catch (err) {
      console.error("Parsing error:", err, item.data);
      return null;
    }
  };

  const processed = data.map(parseRawData).filter(Boolean).reverse();
  const latest = processed.at(-1);

 const getField = (field: string) =>
  processed.map((item: any) => {
    
   return {
  x: item.timestamp,
  y: item?.[field] ?? null,
};

  });


const chartOptions: ChartOptions<"line"> = {
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: false },
  },
  scales: {
    x: {
      type: "time",
      time: {
        tooltipFormat: "yyyy-MM-dd HH:mm:ss",
        unit: "second",
      },
      title: {
        display: true,
        text: "Timestamp (UTC+8)",
      },
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 30,
        callback: (val: any, index: number, ticks: any) => {
          return formatInTimeZone(val, 'UTC', 'HH:mm:ss');
        },
      },
    },
  },
};



  const SensorGroup = ({ title, fields }: { title: string; fields: string[] }) => (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        {fields.map((key) => (
          <div key={key} className="text-sm">
            <div className="text-gray-500 uppercase tracking-wide">{key}</div>
            <div className="text-xl font-bold">{latest?.[key] ?? "N/A"}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const GraphBlock = ({ label, color, field }: { label: string; color: string; field: string }) => {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h4 className="text-md font-semibold text-gray-700 mb-2">{label}</h4>
        <Line
          data={{
            datasets: [
              {
                label,
                data: getField(field),
                borderColor: color,
                backgroundColor: color + "44",
                fill: false,
              },
            ],
          }}
          options={chartOptions}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="text-lg font-medium text-gray-700">Loading...</span>
      </div>
    );
  }

  return (
    <div className="p-6 pt-8 mx-5 space-y-10">
      <header className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 uppercase">Smart Buoy</h1>
        <p className="text-gray-600 mt-2">Logging, Ocean-Based Utilities for Renewable Analytics</p>
      </header>

      {processed.length === 0 ? (
        <p className="text-gray-600">No valid data available.</p>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SensorGroup title="Orientation" fields={["heading", "roll", "pitch"]} />
            <SensorGroup title="Temperature" fields={["box_temp", "water_temp"]} />
            <SensorGroup title="Acceleration" fields={["accel_x", "accel_y", "accel_z"]} />
            <SensorGroup title="Wave Data" fields={["wave_height"]} />
          </div>

          {latest?.timestamp && (
          <p className="text-sm text-gray-500 text-right italic mt-4">
  Last Updated: {new Date(latest.timestamp).toISOString().replace('T', ' ').substring(0, 19)}

</p>

          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <GraphBlock label="Heading" color="#3b82f6" field="heading" />
            <GraphBlock label="Roll" color="#ef4444" field="roll" />
            <GraphBlock label="Pitch" color="#10b981" field="pitch" />
            <GraphBlock label="Box Temp" color="#8b5cf6" field="box_temp" />
            <GraphBlock label="Water Temp" color="#22c55e" field="water_temp" />
            <GraphBlock label="Wave Height" color="#f97316" field="wave_height" />
            <GraphBlock label="Accel X" color="#facc15" field="accel_x" />
            <GraphBlock label="Accel Y" color="#00a099" field="accel_y" />
            <GraphBlock label="Accel Z" color="#eab308" field="accel_z" />
          </div>
        </>
      )}
    </div>
  );
};

export default Soda;
